import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSiteContent } from "../../context/SiteContentContext";
import { HOME_SECTIONS, Lines, SiteLink, thaiDate } from "../../components/site/siteText";
import { apiUrl, getArticles } from "../../services/newspaperApi";
import "./home.css";
import AccountingLogo from "../../assests/images/maneenopkao-acc.png";
import NewsLogo from "../../assests/images/maneenopkao-news.png";
import DefaultHeroImage from "../../images/hero/desk.webp";
import RichText, { plainText } from "../../components/site/RichText";

const COMPANY_LOGOS = { accounting: AccountingLogo, news: NewsLogo };
const pad2 = (n) => String(n + 1).padStart(2, "0");

// Scroll to a section after navigation: /services (own address, HOME_SECTIONS) or an old /#services link
// (HashRouter keeps that anchor in location.hash)
function useScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    const fromPath = location.pathname.replace(/^\//, "");
    const id = HOME_SECTIONS.includes(fromPath) ? fromPath : location.hash ? decodeURIComponent(location.hash.slice(1)) : "";
    if (!id) return;
    // coming from another page, the sections above load (news, pictures) after the first scroll and push the target
    // down — check again a few times and correct the position when it moved
    const scroll = (behavior) => document.getElementById(id)?.scrollIntoView({ behavior, block: "start" });
    const timers = [setTimeout(() => scroll("smooth"), 60)];
    [900, 1800, 3000].forEach((ms) => timers.push(setTimeout(() => {
      const el = document.getElementById(id);
      if (el && Math.abs(el.getBoundingClientRect().top) > 160) scroll("auto");
    }, ms)));
    // stop correcting once the visitor scrolls on their own
    const stop = () => timers.forEach(clearTimeout);
    window.addEventListener("wheel", stop, { once: true, passive: true });
    window.addEventListener("touchmove", stop, { once: true, passive: true });
    return () => {
      stop();
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchmove", stop);
    };
  }, [location.pathname, location.hash, location.key]);
}

function Hero({ c }) {
  const signature = Array.isArray(c.signature) ? c.signature : [];
  return (
    <section className="hero container group-hero">
      <div className="hero-copy">
        <p className="eyebrow">
          <span className="small-line" />
          {c.eyebrow}
        </p>
        <h1>
          <Lines text={c.title} />
          <br />
          <span>{c.titleAccent}</span>
        </h1>
        <p className="hero-description">
          <Lines text={c.description} breakClassName="desktop-break" />
        </p>
        <div className="hero-actions">
          <SiteLink className="button dark" to="/#services">
            {c.primaryLabel}{" "}
            <span aria-hidden="true">↗</span>
          </SiteLink>
          <SiteLink className="text-link" to="/newspaper">
            {c.secondaryLabel}{" "}
            <span aria-hidden="true">→</span>
          </SiteLink>
        </div>
        <div className="group-signature">
          {signature.map((word, i) => (
            <React.Fragment key={word + i}>
              {i > 0 && <i>×</i>}
              <span>{word}</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="hero-art">
        <div className="photo-frame">
          <img src={c.imageUrl || DefaultHeroImage} alt={c.imageAlt || ""} />
          <span className="photo-caption">{c.photoCaption}</span>
        </div>
        <div className="art-tag">
          <span className="art-star" aria-hidden="true">✳</span>
          <div>
            {c.tagLine}
            <br />
            <strong>{c.tagStrong}</strong>
          </div>
        </div>
        <span className="vertical-caption">{c.verticalCaption}</span>
      </div>
    </section>
  );
}

function Principles({ c }) {
  const items = Array.isArray(c.items) ? c.items : [];
  return (
    <div className="principles-bar">
      <div className="container principles-inner">
        <span className="principles-label">{c.label}</span>
        {items.map((item, i) => (
          <div key={i}>
            <span className="point" />
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function LatestNews({ c }) {
  // newest admin news / PR articles first; front-page items from site content fill the rest
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    let active = true;
    getArticles(4).then((d) => active && setArticles(d.articles || [])).catch(() => {});
    return () => { active = false; };
  }, []);

  const fromArticles = articles.map((a) => ({
    date: a.issueDate,
    title: a.title,
    text: a.summary,
    link: `/newspaper/articles/${a.id}`,
    linkLabel: "อ่านข่าว",
    // first picture of the article as the card thumbnail (cards without a picture keep the reference look)
    image: a.files?.[0]?.url ? apiUrl(a.files[0].url) : "",
  }));
  const fromContent = Array.isArray(c.items) ? c.items.filter((it) => it && it.title) : [];
  const items = [...fromArticles, ...fromContent].slice(0, 4);
  return (
    <section className="container section homepage-news" id="latest-news">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{c.eyebrow}</p>
          <h2>
            {c.title}
            <br />
            <span className="serif-accent">{c.titleAccent}</span>
          </h2>
        </div>
        <SiteLink className="text-link" to="/newspaper">{c.linkLabel}</SiteLink>
      </div>

      {items.length === 0 ? (
        <div className="homepage-news-empty">
          <span>{c.emptyLabel}</span>
          {/* admins sometimes paste a whole article here — show its headings / bold properly, never raw # or ** */}
          <div className="homepage-news-empty-text"><RichText text={c.emptyText} /></div>
        </div>
      ) : (
        <div className="homepage-news-grid">
          {items.map((it, i) => (
            <article key={i}>
              {it.image && <img className="homepage-news-thumb" src={it.image} alt="" loading="lazy" />}
              <span className="company-number">{it.date ? thaiDate(it.date) : c.emptyLabel}</span>
              <h3>{it.title}</h3>
              {it.text && <p>{plainText(it.text)}</p>}
              <SiteLink className="text-link" to={it.link || (it.date ? `/newspaper/editions/${it.date}` : "/newspaper")}>
                {it.linkLabel || "อ่านในฉบับรายวัน"}{" "}
                <span>↗</span>
              </SiteLink>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function Group({ c }) {
  const companies = Array.isArray(c.companies) ? c.companies : [];
  const purpose = Array.isArray(c.purpose) ? c.purpose : [];
  return (
    <section className="section container" id="group">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{c.eyebrow}</p>
          <h2>
            {c.title}
            <br />
            <span className="serif-accent">{c.titleAccent}</span>
          </h2>
        </div>
        <p><Lines text={c.description} /></p>
      </div>

      <div className="company-grid">
        {companies.map((co, i) => (
          <article key={i}>
            <div className="company-identity">
              <img
                className={co.logo === "news" ? "company-news-logo" : "company-accounting-logo"}
                src={COMPANY_LOGOS[co.logo] || AccountingLogo}
                alt={co.logoAlt || ""}
              />
            </div>
            <span className="company-number">{co.number}</span>
            <h3><Lines text={co.name} /></h3>
            <p>{co.text}</p>
            <SiteLink className="text-link" to={co.link}>
              {co.linkLabel}{" "}
              <span>↗</span>
            </SiteLink>
          </article>
        ))}
      </div>

      <div className="group-purpose">
        {purpose.map((p, i) => (
          <div key={i}>
            <span>{p.label}</span>
            <h3>{p.title}</h3>
            <p>{p.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Services({ c }) {
  const groups = Array.isArray(c.groups) ? c.groups : [];
  return (
    <section className="services-section" id="services">
      <div className="container section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{c.eyebrow}</p>
            <h2><Lines text={c.title} /></h2>
          </div>
          <p><Lines text={c.description} /></p>
        </div>

        <div className="group-services">
          {groups.map((g, gi) => (
            <article key={gi} id={g.anchor || undefined}>
              <p className="eyebrow">{g.eyebrow}</p>
              <h3>{g.title}</h3>
              {(Array.isArray(g.items) ? g.items : []).map((item, i) => (
                <div className="service-row" key={i}>
                  <span>{pad2(i)}</span>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </div>
              ))}
              <SiteLink className="text-link" to={g.link}>
                {g.linkLabel}{" "}
                <span>↗</span>
              </SiteLink>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function DailyConnection({ c }) {
  return (
    <section className="industry-section">
      <div className="container section newspaper-feature">
        <div>
          <p className="eyebrow">{c.eyebrow}</p>
          <h2>
            <Lines text={c.title} />
            <br />
            <span>{c.titleAccent}</span>
          </h2>
          <p className="feature-description">{c.description}</p>
          <div className="feature-buttons">
            <SiteLink className="button gold" to="/newspaper/submit">
              {c.primaryLabel}{" "}
              <span>↗</span>
            </SiteLink>
            <SiteLink className="text-link light" to="/newspaper">
              {c.secondaryLabel}{" "}
              <span>→</span>
            </SiteLink>
          </div>
          <div className="daily-note">
            <span className="point" />
            {c.note}
          </div>
        </div>

        <SiteLink className="paper-preview" to="/newspaper">
          <div className="paper-topline">{c.paperTopline}</div>
          <h3>{c.paperTitle}</h3>
          <p className="paper-subtitle">{c.paperSubtitle}</p>
          <div className="paper-rule" />
          <div className="paper-date">
            {c.paperDateLeft}
            <span>{c.paperDateRight}</span>
          </div>
          <h4><Lines text={c.paperHeadline} /></h4>
          <div className="preview-columns">
            {[c.paperColumnLeft, c.paperColumnRight].map((label, i) => (
              <div key={i}>
                <span>{label}</span>
                <i /><i /><i /><i /><i />
              </div>
            ))}
          </div>
          <span className="preview-stamp">{c.paperStamp}</span>
          <div className="paper-bottom">
            {c.paperBottom}{" "}
            <span>↗</span>
          </div>
        </SiteLink>
      </div>
    </section>
  );
}

function Journey({ c }) {
  const steps = Array.isArray(c.steps) ? c.steps : [];
  return (
    <section className="container section">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{c.eyebrow}</p>
          <h2><Lines text={c.title} /></h2>
        </div>
        <p><Lines text={c.description} /></p>
      </div>
      <div className="publishing-steps">
        {steps.map((s, i) => (
          <div key={i}>
            <span>{pad2(i)}</span>
            <h3>{s.title}</h3>
            <p>{s.text}</p>
          </div>
        ))}
      </div>
      <div className="publication-note">
        <span>↳</span>
        <p>{c.note}</p>
      </div>
    </section>
  );
}

function buildSummary(form) {
  return [
    `ชื่อ: ${form.name}`,
    form.business ? `กิจการ: ${form.business}` : null,
    `บริการที่สนใจ: ${form.service}`,
    form.problem ? `โจทย์: ${form.problem}` : null,
  ].filter(Boolean).join("\n");
}

function IntakeCard({ c }) {
  const services = Array.isArray(c.formServices) ? c.formServices : [];
  const [form, setForm] = useState({ name: "", business: "", service: services[0] || "", problem: "" });
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // the option list can change after first render (DB content replaces defaults)
  const service = services.includes(form.service) ? form.service : services[0] || "";

  const handleSubmit = (e) => {
    e.preventDefault();
    setSummary(buildSummary({ ...form, service }));
    setStatus("");
  };

  const mailto = `mailto:${c.email || ""}?subject=${encodeURIComponent(
    `ขอคำปรึกษา: ${service}`
  )}&body=${encodeURIComponent(summary)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setStatus("คัดลอกข้อความแล้ว");
    } catch {
      setStatus("คัดลอกไม่สำเร็จ กรุณาเลือกข้อความแล้วคัดลอกเอง");
    }
  };

  return (
    <div className="intake-card">
      <p className="eyebrow">{c.formEyebrow}</p>
      <h3>{c.formTitle}</h3>
      <p className="form-intro">{c.formIntro}</p>
      <form className="site-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <label>
            {c.formNameLabel} <span className="required">*</span>
            <input required value={form.name} onChange={update("name")} autoComplete="name" />
          </label>
          <label>
            {c.formBusinessLabel}
            <input value={form.business} onChange={update("business")} autoComplete="organization" />
          </label>
        </div>
        <label>
          {c.formServiceLabel}
          <select value={service} onChange={update("service")}>
            {services.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label>
          {c.formProblemLabel}
          <textarea rows={4} placeholder={c.formProblemPlaceholder} value={form.problem} onChange={update("problem")} />
        </label>
        <p className="privacy-note">{c.formPrivacy}</p>
        <button type="submit" className="button dark form-submit">
          {c.formSubmitLabel}{" "}
          <span>↗</span>
        </button>
      </form>

      {summary && (
        <div className="intake-result">
          <p className="result-label">SUMMARY READY</p>
          <h4>สรุปสำหรับปรึกษา</h4>
          <pre>{summary}</pre>
          <div className="result-actions">
            <a className="button dark" href={mailto}>
              เปิดแอปอีเมล
              <span>↗</span>
            </a>
            <button type="button" className="text-link" onClick={copy}>
              คัดลอกข้อความ
            </button>
          </div>
          <p role="status">{status}</p>
        </div>
      )}
    </div>
  );
}

function Contact({ c }) {
  const phoneHref = `tel:+66${String(c.phone || "").replace(/\D/g, "").replace(/^0/, "")}`;
  return (
    <section className="contact-section" id="contact">
      <div className="container section contact-layout">
        <div className="contact-copy">
          <p className="eyebrow">{c.eyebrow}</p>
          <h2>
            <Lines text={c.title} />
            <br />
            <span>{c.titleAccent}</span>
          </h2>
          <p><Lines text={c.description} /></p>

          <div className="contact-links">
            <a href={phoneHref}>
              <small>{c.phoneLabel}</small>
              <strong>{c.phone} <span>↗</span></strong>
            </a>
            <a href={`mailto:${c.email}`}>
              <small>{c.emailLabel}</small>
              <span className="email-address">{c.email} ↗</span>
            </a>
            <a href={c.lineUrl} target="_blank" rel="noreferrer">
              <small>{c.lineLabel}</small>
              <strong>{c.lineId} <span>↗</span></strong>
            </a>
          </div>

          <p className="contact-company"><Lines text={c.companies} /></p>

          <address className="contact-address">
            <span>{c.officeLabel}</span>
            <p>{c.address}</p>
            <a className="text-link" href={c.mapUrl} target="_blank" rel="noreferrer">{c.mapLabel}</a>
          </address>

          <p className="contact-hours">
            <strong>{c.hours}</strong>
            <br />
            {c.hoursNote}
          </p>
        </div>

        <IntakeCard c={c} />
      </div>
    </section>
  );
}

function Home() {
  const { siteContent } = useSiteContent();
  useScrollToHash();

  return (
    <main id="main" className="home-page">
      <Hero c={siteContent.homeHero || {}} />
      <Principles c={siteContent.homePrinciples || {}} />
      <LatestNews c={siteContent.homeNews || {}} />
      <Group c={siteContent.homeGroup || {}} />
      <Services c={siteContent.homeServices || {}} />
      <DailyConnection c={siteContent.homeDaily || {}} />
      <Journey c={siteContent.homeJourney || {}} />
      <Contact c={siteContent.homeContact || {}} />
    </main>
  );
}

export default Home;
